import { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AttendanceNotifications from './AttendanceNotifications';
import EditPermissionNotifications from './EditPermissionNotifications';
import PatientViewNotifications from './PatientViewNotifications';
import { 
  LayoutDashboard, 
  Users, 
  UserPlus, 
  ClipboardList, 
  FileText, 
  Calendar, 
  UsersRound, 
  PoundSterling, 
  Shield, 
  Settings, 
  LogOut, 
  Menu, 
  MessageCircle,
  Bell,
  UserCog,
  Briefcase,
  Mail,
  Sparkles,
  FileEdit,
  CreditCard,
  ChevronDown,
  Building,
  PartyPopper,
  Send,
  Percent,
  KeyRound,
  SlidersHorizontal,
  User,
  ShieldAlert,
  UserCheck,
  CalendarDays,
  Brain,
  BarChart3,
  Database,
  Trash2,
  ScrollText,
  Clock,
  Bot,
  TrendingUp,
  MessageSquare,
  Presentation,
  Video
} from 'lucide-react';
import { Button } from './ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuSeparator, 
  DropdownMenuTrigger 
} from './ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './ui/collapsible';
import AIAssistantPanel from './AIAssistantPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_4b79ec74-cc21-403d-a390-656b8c7298f7/artifacts/m95yszhz_1.png";
const ICRCL_ADDRESS = "City Gate House, 246-250 Romford Road, London, E7 9HZ";
const ICRCL_PHONE = "+44 7380 350059";
const ICRCL_EMAIL = "info@icrcl.co.uk";

const DashboardLayout = () => {
  const { user, logout, canAccessFinance, canAccessAudit, api } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [pendingCount, setPendingCount] = useState(0);
  const [clinicalOpen, setClinicalOpen] = useState(true);
  const [adminOpen, setAdminOpen] = useState(true);
  const [financeOpen, setFinanceOpen] = useState(true);

  const fetchPendingCount = async () => {
    try {
      const response = await api.get('/auth/pending-count');
      setPendingCount(response.data.count || 0);
    } catch (error) {
      console.error('Failed to fetch pending count:', error);
    }
  };

  useEffect(() => {
    if (user?.role === 'director' || user?.email?.toLowerCase() === 'admin@icrcl.co.uk') {
      fetchPendingCount();
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isDirector = user?.role === 'director' || user?.role === 'managing_director';
  const isSuperAdmin = user?.email?.toLowerCase() === 'admin@icrcl.co.uk';
  const isAdminStaff = user?.role === 'admin';  // Admin staff - company administration
  const isReception = user?.role === 'reception';  // Reception - appointments & invoices ONLY
  const isFinance = user?.role === 'finance';  // Finance staff - financial management
  const isFinanceAccess = ['director', 'managing_director', 'finance'].includes(user?.role);  // Full finance access
  const isHR = ['director', 'managing_director', 'admin', 'hr'].includes(user?.role);
  const isRehabAssistant = user?.role === 'rehab_assistant';  // Rehab assistants - assist therapists
  const isBusinessDev = user?.role === 'business_development';  // Business Development Manager
  // Clinical access: Director, Clinical Lead, Therapist, Rehab Assistant
  const isClinical = ['director', 'managing_director', 'clinical_lead', 'therapist', 'rehab_assistant'].includes(user?.role);
  // Who can book appointments
  const canAccessAppointments = isClinical || isFinance || isReception;
  // Who can see their earnings
  const canViewEarnings = ['director', 'managing_director', 'clinical_lead', 'therapist', 'rehab_assistant', 'admin', 'finance', 'hr'].includes(user?.role);
  // Check for special grants from Super Admin
  const hasGrant = (grantType) => user?.access_grants?.includes(grantType);

  const NavLinks = ({ mobile = false }) => (
    <nav className="space-y-1">
      {/* Dashboard */}
      <NavLink
        to="/"
        end
        onClick={() => mobile && setSidebarOpen(false)}
        className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
      >
        <LayoutDashboard className="w-5 h-5" />
        <span>Dashboard</span>
      </NavLink>

      {/* Clinical Section - For clinical roles only */}
      {isClinical && (
      <Collapsible open={clinicalOpen} onOpenChange={setClinicalOpen}>
        <CollapsibleTrigger className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600">
          Clinical
          <ChevronDown className={`w-4 h-4 transition-transform ${clinicalOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1">
          <NavLink to="/patients" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <Users className="w-5 h-5" />
            <span>Children & Families</span>
          </NavLink>
          <NavLink to="/assessments" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <ClipboardList className="w-5 h-5" />
            <span>Assessments</span>
          </NavLink>
          <NavLink to="/evaluations" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <FileText className="w-5 h-5" />
            <span>Evaluation Reports</span>
          </NavLink>
          <NavLink to="/progress-notes" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <FileEdit className="w-5 h-5" />
            <span>Progress Notes</span>
          </NavLink>
          <NavLink to="/calendar" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <Calendar className="w-5 h-5" />
            <span>Calendar</span>
          </NavLink>
          <NavLink to="/meetings" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <Video className="w-5 h-5" />
            <span>Video Meetings</span>
            <span className="ml-auto text-xs bg-gradient-to-r from-[#2a9d8f] to-[#1e3a5f] text-white px-1.5 py-0.5 rounded">New</span>
          </NavLink>
          <NavLink to="/meeting-records" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <FileText className="w-5 h-5" />
            <span>Meeting Records</span>
          </NavLink>
          <NavLink to="/events" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <PartyPopper className="w-5 h-5" />
            <span>Events & Activities</span>
          </NavLink>
        </CollapsibleContent>
      </Collapsible>
      )}

      {/* Administration Section */}
      <Collapsible open={adminOpen} onOpenChange={setAdminOpen}>
        <CollapsibleTrigger className="w-full flex items-center justify-between px-4 py-2 text-xs font-semibold text-slate-400 uppercase tracking-wider hover:text-slate-600">
          Administration
          <ChevronDown className={`w-4 h-4 transition-transform ${adminOpen ? 'rotate-180' : ''}`} />
        </CollapsibleTrigger>
        <CollapsibleContent className="space-y-1">
          {/* AI Analytics Dashboard - Directors Only */}
          {isDirector && (
            <NavLink to="/analytics" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Brain className="w-5 h-5" />
              <span>AI Analytics</span>
              {isSuperAdmin && <span className="ml-auto text-xs bg-amber-500 text-white px-1.5 py-0.5 rounded">Admin</span>}
            </NavLink>
          )}
          {(isDirector || isAdminStaff) && (
            <NavLink to="/staff" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <UsersRound className="w-5 h-5" />
              <span>Staff Directory</span>
            </NavLink>
          )}
          {(isDirector || isAdminStaff) && (
            <NavLink to="/professionals" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Building className="w-5 h-5" />
              <span>External Professionals</span>
            </NavLink>
          )}
          {isHR && (
            <NavLink to="/hr" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Briefcase className="w-5 h-5" />
              <span>HR & Workforce</span>
            </NavLink>
          )}
          {/* DBS Tracking - for HR/Admin/Directors */}
          {isHR && (
            <NavLink to="/dbs-tracking" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Shield className="w-5 h-5" />
              <span>DBS Tracking</span>
            </NavLink>
          )}
          {/* Rota Management - for HR/Admin/Directors */}
          {(isHR || isDirector) && (
            <NavLink to="/rota" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Clock className="w-5 h-5" />
              <span>Rota Management</span>
            </NavLink>
          )}
          {/* My HR Profile - available to ALL staff */}
          {!isHR && (
            <NavLink to="/my-hr" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Briefcase className="w-5 h-5" />
              <span>HR & Workforce</span>
            </NavLink>
          )}
          {/* Attendance - available to ALL staff */}
          <NavLink to="/attendance" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <Clock className="w-5 h-5" />
            <span>Attendance</span>
          </NavLink>
          {/* My Profile - available to ALL staff */}
          <NavLink to="/my-profile" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <User className="w-5 h-5" />
            <span>My Profile</span>
          </NavLink>
          {isSuperAdmin && (
            <NavLink to="/users" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <UserCog className="w-5 h-5" />
              <span>User Management</span>
              {pendingCount > 0 && (
                <span className="ml-auto bg-amber-500 text-white text-xs px-2 py-0.5 rounded-full">{pendingCount}</span>
              )}
            </NavLink>
          )}
          
          {/* RECEPTION - Limited access to Finance (Overview, Invoices, % Sessions only) */}
          {isReception && (
            <>
              <NavLink to="/calendar" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <Calendar className="w-5 h-5" />
                <span>Appointments</span>
              </NavLink>
              <NavLink to="/finance" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <PoundSterling className="w-5 h-5" />
                <span>Finance Management</span>
              </NavLink>
            </>
          )}
          
          {/* ADMIN - Company administration */}
          {isAdminStaff && (
            <>
              <NavLink to="/staff" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <Users className="w-5 h-5" />
                <span>Staff Directory</span>
              </NavLink>
              <NavLink to="/events" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <CalendarDays className="w-5 h-5" />
                <span>Events</span>
              </NavLink>
            </>
          )}
          
          {/* BUSINESS DEVELOPMENT MANAGER - Staff Directory, Safeguarding, Events, NO clinical/finance */}
          {isBusinessDev && (
            <>
              <NavLink to="/staff" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <UsersRound className="w-5 h-5" />
                <span>Staff Directory</span>
              </NavLink>
              <NavLink to="/professionals" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <Building className="w-5 h-5" />
                <span>External Professionals</span>
              </NavLink>
              <NavLink to="/crm" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <TrendingUp className="w-5 h-5" />
                <span>CRM Dashboard</span>
              </NavLink>
              <NavLink to="/events" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <CalendarDays className="w-5 h-5" />
                <span>Events & Activities</span>
              </NavLink>
              <NavLink to="/safeguarding" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                <ShieldAlert className="w-5 h-5" />
                <span>Safeguarding</span>
              </NavLink>
              {/* Show granted access items */}
              {hasGrant('clinical_access') && (
                <NavLink to="/patients" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                  <Users className="w-5 h-5" />
                  <span>Children & Families</span>
                </NavLink>
              )}
              {hasGrant('finance_access') && (
                <NavLink to="/finance" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                  <PoundSterling className="w-5 h-5" />
                  <span>Finance</span>
                </NavLink>
              )}
            </>
          )}
          
          {/* Full Finance Section - Directors and Finance ONLY */}
          {isFinanceAccess && (
            <Collapsible open={financeOpen} onOpenChange={setFinanceOpen}>
              <CollapsibleTrigger className="sidebar-link w-full justify-between group">
                <div className="flex items-center gap-3">
                  <PoundSterling className="w-5 h-5" />
                  <span>Finance</span>
                </div>
                <ChevronDown className={`w-4 h-4 transition-transform ${financeOpen ? 'rotate-180' : ''}`} />
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4 space-y-1">
                <NavLink to="/finance" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                  <FileText className="w-4 h-4" />
                  <span>Finance Manager</span>
                </NavLink>
                <NavLink to="/rti" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                  <Send className="w-4 h-4" />
                  <span>HMRC RTI</span>
                </NavLink>
                {isDirector && (
                  <NavLink to="/finance/accounts" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                    <FileEdit className="w-4 h-4" />
                    <span>Accounts Excel</span>
                  </NavLink>
                )}
                <NavLink to="/payroll" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
                  <CreditCard className="w-4 h-4" />
                  <span>Payroll Manager</span>
                </NavLink>
              </CollapsibleContent>
            </Collapsible>
          )}
          
          {/* My Earnings - For all professionals who can earn */}
          {canViewEarnings && !isFinanceAccess && (
            <NavLink to="/my-earnings" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <PoundSterling className="w-5 h-5" />
              <span>My Earnings</span>
            </NavLink>
          )}
          <NavLink to="/messages" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <Mail className="w-5 h-5" />
            <span>Messages & Email</span>
          </NavLink>
          {/* Safeguarding - Clinical, Admin roles, and Business Development */}
          {(isClinical || isDirector || isSuperAdmin || isBusinessDev) && (
            <NavLink to="/safeguarding" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <ShieldAlert className="w-5 h-5" />
              <span>Safeguarding</span>
            </NavLink>
          )}
          {/* Parent Portal - For Parent Users */}
          {(user?.role === 'parent' || user?.is_parent_portal_user) && (
            <NavLink to="/parent-portal" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <UserCheck className="w-5 h-5" />
              <span>My Portal</span>
              <span className="ml-auto text-xs bg-gradient-to-r from-[#2a9d8f] to-[#1e3a5f] text-white px-1.5 py-0.5 rounded">Parent</span>
            </NavLink>
          )}
          {/* Parent Management - Admin and Director */}
          {(isDirector || isSuperAdmin) && (
            <NavLink to="/parent-management" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Users className="w-5 h-5" />
              <span>Parent Management</span>
            </NavLink>
          )}
          {/* Feedback Management - Admin and Director */}
          {(isDirector || isSuperAdmin) && (
            <NavLink to="/feedback-management" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <MessageSquare className="w-5 h-5" />
              <span>Feedback</span>
            </NavLink>
          )}
          {/* Policy - All users can view, Super Admin can manage */}
          <NavLink to="/policy" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <FileText className="w-5 h-5" />
            <span>Policy</span>
          </NavLink>
          {isDirector && (
            <NavLink to="/audit" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Shield className="w-5 h-5" />
              <span>Audit Logs</span>
            </NavLink>
          )}
          {isSuperAdmin && (
            <NavLink to="/access-management" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <KeyRound className="w-5 h-5" />
              <span>Access Management</span>
            </NavLink>
          )}
          {isSuperAdmin && (
            <NavLink to="/form-config" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <SlidersHorizontal className="w-5 h-5" />
              <span>Form Configuration</span>
            </NavLink>
          )}
          {isDirector && (
            <NavLink to="/data-management" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Database className="w-5 h-5" />
              <span>Data Management</span>
            </NavLink>
          )}
          {isSuperAdmin && (
            <NavLink to="/policy-templates" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <ScrollText className="w-5 h-5" />
              <span>Policy Templates</span>
            </NavLink>
          )}
          {/* AI Workforce - Super Admin ONLY */}
          {isSuperAdmin && (
            <NavLink to="/ai-workforce" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Bot className="w-5 h-5" />
              <span>AI Workforce</span>
              <span className="ml-auto text-xs bg-purple-500 text-white px-1.5 py-0.5 rounded">AI</span>
            </NavLink>
          )}
          {/* AI Assistant - Super Admin ONLY */}
          {isSuperAdmin && (
            <NavLink to="/ai-assistant" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <MessageSquare className="w-5 h-5" />
              <span>AI Assistant</span>
              <span className="ml-auto text-xs bg-gradient-to-r from-purple-500 to-indigo-500 text-white px-1.5 py-0.5 rounded">Chat</span>
            </NavLink>
          )}
          {/* AI Presentation Builder - Super Admin ONLY */}
          {isSuperAdmin && (
            <NavLink to="/ai-presentations" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <Presentation className="w-5 h-5" />
              <span>AI Presentations</span>
              <span className="ml-auto text-xs bg-gradient-to-r from-amber-500 to-orange-500 text-white px-1.5 py-0.5 rounded">New</span>
            </NavLink>
          )}
          {/* CRM Dashboard - Super Admin and Business Development */}
          {isSuperAdmin && (
            <NavLink to="/crm" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
              <TrendingUp className="w-5 h-5" />
              <span>CRM Dashboard</span>
            </NavLink>
          )}
          <NavLink to="/settings" onClick={() => mobile && setSidebarOpen(false)} className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}>
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </NavLink>
        </CollapsibleContent>
      </Collapsible>
    </nav>
  );

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-slate-200">
        <div className="flex items-center gap-3 px-6 py-5 border-b border-slate-100">
          <img src={LOGO_URL} alt="ICRCL Logo" className="w-12 h-12 object-contain" />
          <div>
            <h1 className="font-bold text-[#1e3a5f] text-lg tracking-tight">ICRCL</h1>
            <p className="text-xs text-slate-500">Healthcare Platform</p>
          </div>
        </div>
        
        <div className="flex-1 overflow-y-auto px-3 py-4">
          <NavLinks />
        </div>

        {/* AI Assistant Button */}
        <div className="p-4 border-t border-slate-100">
          <Button
            onClick={() => setAiPanelOpen(true)}
            className="w-full bg-gradient-to-r from-[#2a9d8f] to-[#1e3a5f] hover:opacity-90 text-white rounded-full"
            data-testid="ai-assistant-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            AI Assistant
          </Button>
        </div>

        {/* Footer Info */}
        <div className="p-4 border-t border-slate-100 text-xs text-slate-400">
          <p className="flex items-center gap-1"><Building className="w-3 h-3" /> {ICRCL_ADDRESS}</p>
          <p className="mt-1">{ICRCL_EMAIL} | {ICRCL_PHONE}</p>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-72 p-0">
          <SheetHeader className="px-6 py-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="ICRCL Logo" className="w-10 h-10 object-contain" />
              <div className="text-left">
                <h2 className="font-bold text-[#1e3a5f]">ICRCL</h2>
                <p className="text-xs text-slate-500">Healthcare Platform</p>
              </div>
            </div>
          </SheetHeader>
          <div className="px-3 py-4 overflow-y-auto max-h-[calc(100vh-200px)]">
            <NavLinks mobile />
          </div>
          <div className="p-4 border-t">
            <Button
              onClick={() => { setAiPanelOpen(true); setSidebarOpen(false); }}
              className="w-full bg-gradient-to-r from-[#2a9d8f] to-[#1e3a5f] text-white rounded-full"
            >
              <Sparkles className="w-4 h-4 mr-2" />
              AI Assistant
            </Button>
          </div>
        </SheetContent>
      </Sheet>

      {/* Main Content */}
      <div className="flex-1 lg:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b border-slate-200">
          <div className="flex items-center justify-between px-4 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                className="lg:hidden"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="w-5 h-5" />
              </Button>
              <div className="lg:hidden flex items-center gap-2">
                <img src={LOGO_URL} alt="ICRCL" className="w-8 h-8" />
                <span className="font-bold text-[#1e3a5f]">ICRCL</span>
              </div>
            </div>

            <div className="flex items-center gap-3">
              {/* AI Assistant Button - Desktop */}
              <Button
                variant="outline"
                size="sm"
                className="hidden md:flex rounded-full border-[#2a9d8f] text-[#2a9d8f] hover:bg-[#2a9d8f] hover:text-white"
                onClick={() => setAiPanelOpen(true)}
              >
                <Sparkles className="w-4 h-4 mr-2" />
                AI Assistant
              </Button>

              {/* Messages */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/messages')}
              >
                <Mail className="w-5 h-5 text-slate-600" />
              </Button>

              {/* Notifications */}
              <Button
                variant="ghost"
                size="icon"
                className="relative"
                onClick={() => pendingCount > 0 && navigate('/users')}
                data-testid="notifications-btn"
              >
                <Bell className="w-5 h-5 text-slate-600" />
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
                    {pendingCount}
                  </span>
                )}
              </Button>

              {/* Attendance Notifications - for HR/Admin/Directors */}
              <AttendanceNotifications />

              {/* Edit Permission Notifications - for approvers */}
              <EditPermissionNotifications />

              {/* Patient View Notifications - for Lead Therapists */}
              <PatientViewNotifications />

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-3 px-2" data-testid="user-menu-btn">
                    <Avatar className="w-9 h-9">
                      {(user?.profile_image_data || user?.profile_image) && (
                        <AvatarImage 
                          src={user?.profile_image_data || (user?.profile_image?.startsWith('/api') ? `${API_URL}${user.profile_image}` : user?.profile_image)} 
                          alt={`${user?.first_name} ${user?.last_name}`}
                        />
                      )}
                      <AvatarFallback className="bg-[#1e3a5f] text-white text-sm">
                        {user?.first_name?.[0]}{user?.last_name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-slate-900">
                        {user?.first_name} {user?.last_name}
                      </p>
                      <p className="text-xs text-slate-500 capitalize">
                        {user?.role?.replace(/_/g, ' ')}
                      </p>
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <div className="px-3 py-2">
                    <p className="text-sm font-medium">{user?.email}</p>
                    <p className="text-xs text-slate-500 capitalize">{user?.role?.replace(/_/g, ' ')}</p>
                    {user?.job_title && <p className="text-xs text-slate-400">{user?.job_title}</p>}
                  </div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="w-4 h-4 mr-2" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setAiPanelOpen(true)}>
                    <Sparkles className="w-4 h-4 mr-2" />
                    AI Assistant
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                    <LogOut className="w-4 h-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="px-4 lg:px-8 py-4 border-t border-slate-200 text-center text-xs text-slate-400">
          <p>© 2025 Innovative Care and Rehab Centre Limited (ICRCL)</p>
          <p>{ICRCL_EMAIL} | {ICRCL_PHONE} | {ICRCL_ADDRESS}</p>
        </footer>
      </div>

      {/* AI Assistant Panel */}
      <AIAssistantPanel open={aiPanelOpen} onOpenChange={setAiPanelOpen} />
    </div>
  );
};

export default DashboardLayout;
